(function (scope) {
  'use strict';
  const MAX_REVIEWS = 250;
  function htmlText(html) {
    if (typeof html !== 'string' || !scope.document) throw new Error('Missing review text');
    const template = scope.document.createElement('template');
    template.innerHTML = html;
    const content = template.content;
    content.querySelectorAll('script,style,iframe,object,embed').forEach(node => node.remove());
    content.querySelectorAll('br').forEach(node => node.replaceWith('\n'));
    content.querySelectorAll('p,div,li,h1,h2,h3,h4,blockquote').forEach(node => node.append('\n'));
    return Array.from(content.childNodes, node => node.textContent || '').join('').trim();
  }
  function productPath(value, storeOrigin) {
    const url = new URL(value, storeOrigin);
    if (url.origin !== new URL(storeOrigin).origin || !/^\/products\/[a-z0-9-]+\/?$/i.test(url.pathname)) throw new Error('Invalid product association');
    return url.pathname;
  }
  function imageURL(value, catalog) {
    if (!value) return '';
    const url = new URL(value);
    const allowed = catalog ? ['cdn.shopify.com'] : ['cdn.shopify.com', 'review-images.judgeme.com'];
    if (url.protocol !== 'https:' || !allowed.includes(url.hostname)) throw new Error('Invalid image URL');
    return url.href;
  }
  function validatePage(raw, productId, page, storeOrigin) {
    if (!raw || String(raw.product_external_id) !== String(productId)) throw new Error('Wrong product dataset');
    const count = raw.number_of_reviews, pagination = raw.pagination;
    if (!Number.isInteger(count) || count < 0 || count > MAX_REVIEWS || !Array.isArray(raw.reviews)) throw new Error('Unsupported review count');
    if (!pagination || pagination.current_page !== page || !Number.isInteger(pagination.total_pages) || pagination.total_pages < 1 || pagination.total_pages > 50) throw new Error('Invalid pagination');
    const average = Number(raw.average_rating);
    if (!Number.isFinite(average) || average < 0 || average > 5) throw new Error('Invalid average');
    const reviews = raw.reviews.map(r => {
      if (!r || typeof r.uuid !== 'string' || !r.uuid || !Number.isInteger(r.rating) || r.rating < 1 || r.rating > 5 || (typeof r.body !== 'string' && typeof r.body_html !== 'string') || typeof r.product_title !== 'string' || !r.product_title.trim() || typeof r.reviewer_name !== 'string' || !Number.isFinite(Date.parse(r.created_at))) throw new Error('Incomplete review record');
      if ((r.video_external_ids || []).length || (r.media_platform_hosted_video_infos || []).length || (r.cf_answers || []).length) throw new Error('Use native widget for rich review features');
      return {
        uuid: r.uuid, rating: r.rating, body: typeof r.body === 'string' ? r.body : htmlText(r.body_html), title: typeof r.title === 'string' ? r.title : '',
        name: r.reviewer_name, date: r.created_at, verified: r.verified_buyer === true,
        product: r.product_title, path: productPath(r.product_url, storeOrigin), image: imageURL(r.product_variant_image_url, true),
        pictures: (r.pictures_urls || []).map(p => imageURL(p.original, false)),
        reply: typeof r.reply_content === 'string' ? htmlText(r.reply_content) : ''
      };
    });
    return { count, average, pages: pagination.total_pages, reviews };
  }
  function combinePages(pages) {
    const first = pages[0];
    if (!first || pages.length !== first.pages || pages.some(p => p.count !== first.count || p.average !== first.average || p.pages !== first.pages)) throw new Error('Dataset changed while loading');
    const reviews = pages.flatMap(p => p.reviews);
    if (reviews.length !== first.count || new Set(reviews.map(r => r.uuid)).size !== reviews.length) throw new Error('Incomplete or duplicate dataset');
    const average = reviews.length ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;
    if (Math.abs(average - first.average) > 0.015) throw new Error('Rating mismatch');
    return { count: first.count, average: first.average, reviews };
  }
  function selectReviews(reviews, filter) {
    const selected = reviews.filter(r => (!filter.rating || r.rating === Number(filter.rating)) && (!filter.product || r.path === filter.product) && (!filter.photos || r.pictures.length > 0) && (!filter.search || (r.body + ' ' + r.title + ' ' + r.product).toLowerCase().includes(filter.search.toLowerCase())));
    const byDate = (a, b) => Date.parse(b.date) - Date.parse(a.date) || a.uuid.localeCompare(b.uuid);
    selected.sort(filter.sort === 'oldest' ? (a, b) => -byDate(a, b) : filter.sort === 'highest' ? (a, b) => b.rating - a.rating || byDate(a, b) : filter.sort === 'lowest' ? (a, b) => a.rating - b.rating || byDate(a, b) : byDate);
    return selected;
  }
  const api = { validatePage, combinePages, selectReviews, productPath, imageURL, htmlText };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!scope.customElements || scope.customElements.get('rt-review-test')) return;
  class ReviewTest extends scope.HTMLElement {
    connectedCallback() {
      if (this._active) return;
      this._active = true;
      this._controller = new AbortController();
      this._filter = { sort: 'recent', rating: 0, product: '', photos: false, search: '' };
      this._page = 1;
      this._standard = false;
      this._native = this.ownerDocument.getElementById(this.dataset.nativeId);
      this._ui = this.querySelector('[data-review-ui]');
      this._tools = this.querySelector('[data-review-tools]');
      const controller = this._controller;
      this.dataset.state = 'loading';
      const timeout = setTimeout(() => controller.abort(), 15000);
      this._timeout = timeout;
      this.load(controller).then(data => {
        if (controller !== this._controller || controller.signal.aborted || !this.isConnected) return;
        this._data = data;
        this.render();
        this._ui.hidden = false;
        this._native.hidden = true;
        this._tools.hidden = false;
        this.dataset.state = 'ready';
      }).catch(() => {
        if (controller === this._controller) this.fallback();
      }).finally(() => clearTimeout(timeout));
    }
    disconnectedCallback() {
      this._controller?.abort();
      clearTimeout(this._timeout);
      this._active = false;
      if (this._native) this._native.hidden = false;
      if (this._ui) this._ui.hidden = true;
    }
    async load(controller) {
      if (!this._native || !this._ui || !this._tools) throw new Error('Missing native fallback');
      this._strings = JSON.parse(this.querySelector('[data-review-strings]').textContent);
      const seed = JSON.parse(this.querySelector('[data-review-seed]').textContent);
      if (!seed || String(seed.product_external_id) !== this.dataset.productId || typeof seed.metafield_updated_at !== 'string') throw new Error('Missing review seed');
      const request = async page => {
        const url = new URL('https://cdn.judge.me/reviews/reviews_for_widget');
        url.search = new URLSearchParams({ product_id: this.dataset.productId, page: String(page), sort_by: 'created_at', sort_dir: 'desc', skip_other_languages: 'true', widget_theme: 'standard', ts: seed.metafield_updated_at, shop_domain: this.dataset.shop, platform: 'shopify' });
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit', mode: 'cors', cache: 'no-store' });
        if (!response.ok) throw new Error('Review source unavailable');
        return validatePage(await response.json(), this.dataset.productId, page, this.dataset.storeOrigin);
      };
      const first = await request(1), pages = [first];
      for (let page = 2; page <= first.pages; page++) pages.push(await request(page));
      return combinePages(pages);
    }
    fallback() {
      this.dataset.state = 'fallback';
      if (this._native) this._native.hidden = false;
      if (this._ui) this._ui.hidden = true;
      if (this._tools) this._tools.hidden = true;
    }
    node(tag, text, className) {
      const element = this.ownerDocument.createElement(tag);
      if (text !== undefined) element.textContent = text;
      if (className) element.className = className;
      return element;
    }
    select(label, choices, value, change) {
      const wrapper = this.node('label', undefined, 'rt-review-test__control');
      wrapper.append(this.node('span', label));
      const select = this.node('select');
      select.setAttribute('aria-label', label);
      for (const [optionValue, text] of choices) {
        const option = this.node('option', text);
        option.value = optionValue;
        select.append(option);
      }
      select.value = String(value);
      select.addEventListener('change', () => { change(select.value); this._page = 1; this.renderList(); });
      wrapper.append(select);
      return wrapper;
    }
    render() {
      const s = this._strings, data = this._data;
      this._ui.replaceChildren();
      const heading = this.node('h2', s.heading, 'rt-review-test__heading');
      const summary = this.node('p', data.average.toFixed(1) + ' / 5 · ' + (data.count === 1 ? s.one : s.other).replace('__COUNT__', data.count), 'rt-review-test__summary');
      this._ui.append(heading, summary);
      const controls = this.node('div', undefined, 'rt-review-test__controls');
      controls.append(this.select(s.sort, [['recent', s.recent], ['oldest', s.oldest], ['highest', s.highest], ['lowest', s.lowest]], this._filter.sort, value => this._filter.sort = value));
      controls.append(this.select(s.rating, [['0', s.allRatings], ...[5, 4, 3, 2, 1].map(n => [String(n), String(n) + ' / 5'])], this._filter.rating, value => this._filter.rating = Number(value)));
      const products = [...new Map(data.reviews.map(r => [r.path, r.product])).entries()];
      controls.append(this.select(s.product, [['', s.allProducts], ...products], this._filter.product, value => this._filter.product = value));
      controls.append(this.select(s.media, [['all', s.allMedia], ['photos', s.photos]], this._filter.photos ? 'photos' : 'all', value => this._filter.photos = value === 'photos'));
      this._ui.append(controls);
      this._status = this.node('p', '', 'rt-review-test__status');
      this._status.setAttribute('role', 'status');
      this._list = this.node('div', undefined, 'rt-review-test__list');
      this._pagination = this.node('nav', undefined, 'rt-review-test__pagination');
      this._pagination.setAttribute('aria-label', s.pagination);
      this._ui.append(this._status, this._list, this._pagination);
      this._tools.textContent = s.standard;
      this._tools.onclick = () => {
        this._standard = !this._standard;
        this._native.hidden = !this._standard;
        this._ui.hidden = this._standard;
        this._tools.textContent = this._standard ? s.return : s.standard;
      };
      this.renderList();
    }
    renderList() {
      const s = this._strings;
      const reviews = selectReviews(this._data.reviews, this._filter), totalPages = Math.max(1, Math.ceil(reviews.length / 5));
      this._page = Math.min(this._page, totalPages);
      const shown = Math.min(5, Math.max(0, reviews.length - (this._page - 1) * 5));
      this._status.textContent = s.showing.replace('__SHOWN__', shown).replace('__COUNT__', this._data.count);
      this._list.replaceChildren();
      if (!reviews.length) this._list.append(this.node('p', s.noMatches));
      for (const r of reviews.slice((this._page - 1) * 5, this._page * 5)) {
        const article = this.node('article', undefined, 'rt-review-test__review');
        article.dataset.reviewId = r.uuid;
        const stars = this.node('p', '★'.repeat(r.rating) + '☆'.repeat(5 - r.rating), 'rt-review-test__stars');
        stars.setAttribute('aria-label', r.rating + ' / 5');
        const author = this.node('p', r.name, 'rt-review-test__author');
        if (r.verified) author.append(this.node('span', s.verified, 'rt-review-test__verified'));
        const date = this.node('time', new Date(r.date).toLocaleDateString('en-US'));
        date.dateTime = r.date;
        article.append(stars, author, date);
        if (r.title) article.append(this.node('h3', r.title, 'rt-review-test__title'));
        article.append(this.node('p', r.body, 'rt-review-test__body'));
        if (r.pictures.length) {
          const gallery = this.node('div', undefined, 'rt-review-test__photos');
          for (const url of r.pictures) {
            const link = this.node('a'); link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer';
            const image = this.node('img'); image.src = url; image.alt = s.customerPhoto; image.width = 96; image.height = 96; image.loading = 'lazy';
            image.onerror = () => { link.hidden = true; };
            link.append(image); gallery.append(link);
          }
          article.append(gallery);
        }
        if (r.reply) article.append(this.node('p', r.reply, 'rt-review-test__reply'));
        const attribution = this.node('div', undefined, 'rt-review-test__product');
        if (r.image) {
          const link = this.node('a'); link.href = r.path; link.className = 'rt-review-test__image-link';
          const image = this.node('img'); image.src = r.image; image.alt = s.catalogPhoto + ': ' + r.product; image.width = 48; image.height = 48; image.loading = 'lazy';
          image.onerror = () => { link.hidden = true; };
          link.append(image); attribution.append(link);
        }
        const details = this.node('div');
        details.append(this.node('p', r.verified ? s.purchased : s.reviewFor, 'rt-review-test__product-label'));
        const link = this.node('a', r.product, 'rt-review-test__product-name'); link.href = r.path;
        details.append(link); attribution.append(details); article.append(attribution);
        this._list.append(article);
      }
      this._pagination.replaceChildren();
      const previous = this.node('button', s.previous), next = this.node('button', s.next);
      previous.type = next.type = 'button';
      previous.disabled = this._page <= 1; next.disabled = this._page >= totalPages;
      previous.onclick = () => { this._page--; this.renderList(); this._list.scrollIntoView({ block: 'start' }); };
      next.onclick = () => { this._page++; this.renderList(); this._list.scrollIntoView({ block: 'start' }); };
      this._pagination.append(previous, this.node('span', this._page + ' / ' + totalPages), next);
    }
  }
  scope.customElements.define('rt-review-test', ReviewTest);
})(typeof window !== 'undefined' ? window : globalThis);
